import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  Mail,
  MailPlus,
  RefreshCw,
  SendHorizontal,
  Sparkles,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { FiLoader } from "react-icons/fi";
import { toast } from "sonner";
import {
  createNewEmailTemplate,
  fetchAllEmailTemplates,
  saveCustomEmailTemplate,
  updateCustomEmailTemplate,
} from "../../Redux/ReduxSlices/EmailTemplateSlice";
import {
  createCampaignAPi,
  sendBulkCampaignApi,
  sendTrialEmail,
} from "../../Redux/ReduxSlices/campaignSlice";
import { fetchLeadBatchItemsBySession } from "../../Redux/ReduxSlices/leadBatchSlice";
import { fetchUserDetails } from "../../Redux/ReduxSlices/profileSlice";
import EmailConnectModal from "../../components/campaigns/EmailConnectModal";
import NormalLoader from "../../components/customeLoader/NormalLoader";
import Footer from "../../components/layout/Footer";
import Header from "../../components/layout/Header";
import HowToUseEmailTemplate from "../howToUse/EmailTemplateHowToUse";

import gamilIcon from "../../assets/images/gmail.webp";
import microsoftIcon from "../../assets/images/outlook.webp";
import smtp from "../../assets/images/smtp.svg";
import zohoIcon from "../../assets/images/zohomail.png";

import yahooIcon from "../../assets/images/yahoopng.png";
import { useAppContext } from "../../context/AppContext";

/* --------------------------- DRAFT PERSISTENCE --------------------------- */
const DRAFT_KEY = "campaign_draft_v1";

function saveDraft(draft) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {}
}

function loadDraft() {
  try {
    const v = sessionStorage.getItem(DRAFT_KEY);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

// 🔴 INTERNAL clear function used in this file
function clearDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {}
}

// ✅ EXPORTED helper – use this in your logout logic
export function clearCampaignDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {}
}

/* --------------------------- Tiny WYSIWYG editor --------------------------- */
function HtmlEditor({
  value,
  onChange,
  placeholder = "Type here...",
  minHeight = 200,
  className = "",
  sanitize,
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const safe = sanitize ? sanitize(value || "") : value || "";
    if (ref.current.innerHTML !== safe) {
      ref.current.innerHTML = safe;
    }
  }, [value, sanitize]);

  const handleInput = (e) => {
    const html = e.currentTarget.innerHTML;
    const safe = sanitize ? sanitize(html) : html;
    onChange(safe);
  };

  const base =
    "rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-green-500 bg-white p-3 outline-none editor-content";

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      className={`${base} ${className}`}
      style={{
        minHeight,
        whiteSpace: "normal",
        lineHeight: 1.6,
        cursor: "text",
      }}
      data-placeholder={placeholder}
      onBlur={handleInput}
    />
  );
}

/* --------------------------- Main component --------------------------- */

export default function CreateCampaignStepper() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [bulkSendLoading, setBulkSendLoading] = useState(false);
  const [hasContentChanged, setHasContentChanged] = useState(true);
  const sessionId = localStorage.getItem("sessionId");

  const {
    promptInput,
    setPromptInput,
    promptsDataCompany,
    promptsDataPeople,
    viewMode,
  } = useAppContext();
  const [sendPromptInput, setSendPromptInput] = useState(true);

  useEffect(() => {
    if (viewMode == "companies" && promptsDataCompany == null) {
      setSendPromptInput(false);
    } else if (viewMode == "people" && promptsDataPeople.data.length == 0) {
      setSendPromptInput(false);
    } else {
      setSendPromptInput(true);
    }
  }, []);

  useEffect(() => {
    dispatch(fetchAllEmailTemplates());
    dispatch(fetchUserDetails());
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { details: userData } = useSelector((state) => state.profile);
  const isEmailConnected = userData?.data?.is_email_connected;
  const providerSummary = userData?.provider_summary;

  const userId = userData?.data?.id;

  const connectedProviders = providerSummary
    ? Object.entries(providerSummary).filter(
        ([, provider]) => provider && provider.connected,
      )
    : [];

  const { templates: emailTemplates = [], loading: isLoading } = useSelector(
    (state) => state.customEmail || {},
  );

  const [step, setStep] = useState(1);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [showEmailConnect, setShowEmailConnect] = useState(false);
  // const [loading, setLoading] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showSendConfirmModal, setShowSendConfirmModal] = useState(false);
  const [showOAuthPopup, setShowOAuthPopup] = useState(false);
  const [spin, setSpin] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [selectedTab, setSelectedTab] = useState("custom");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  // RAW HTML values (send these to API)
  const [subjectData, setSubjectData] = useState("");
  const [bodyData, setBodyData] = useState(""); // RAW HTML
  const [footerData, setFooterData] = useState(""); // RAW HTML

  // PREVIEW (sanitized)
  const [bodyHtml, setBodyHtml] = useState("");
  const [footerHtml, setFooterHtml] = useState("");
  const [compiledEmailHtml, setCompiledEmailHtml] = useState("");
  const [templateSaved, setTemplateSaved] = useState(false);
  // AI
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiWebsiteUrl, setAiWebsiteUrl] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiReady, setAiReady] = useState(false);

  // email save
  const [isSaving, setIsSaving] = useState(false);

  // for how to use email template
  const [showHowTo, setShowHowTo] = useState(false);
  // recipients (example)

  const { itemsBySession, loading, error } = useSelector(
    (state) => state.leadbatch,
  );

  // useEffect(() => {
  //   window.scrollTo(0, 0);
  // }, []);

  useEffect(() => {
    dispatch(fetchLeadBatchItemsBySession());
  }, []);

  const cleanText = (text) => {
    return text
      ?.replace(/<[^>]*>/g, "") // remove HTML tags
      ?.replace(/&nbsp;/g, " ") // remove nbsp
      ?.trim();
  };

  const leadItems = itemsBySession?.items || [];
  // useEffect(() => {
  //   if (location.state?.selectedPeople) {
  //     setSelectedPeople(location.state.selectedPeople);
  //   } else {
  //     setSelectedPeople([]);
  //   }
  // }, [location.state]);

  // ✅ safer default provider selection
  useEffect(() => {
    if (connectedProviders.length > 0) {
      setSelectedProvider((prev) => prev || connectedProviders[0][0]);
    }
  }, [connectedProviders]);

  // 🔍 debug: track provider changes
  useEffect(() => {
    if (selectedProvider) {
    }
  }, [selectedProvider]);

  // useEffect(() => {
  //   const BACKEND_ORIGIN = "https://api.maxipitch.com";

  //   const onMessage = (event) => {
  //     if (event.origin !== BACKEND_ORIGIN) return;

  //     if (event.data?.type === "oauth_success") {
  //       // refresh user data
  //       dispatch(fetchUserDetails());

  //       // 🔥 THIS LINE DOES THE REFRESH
  //       window.location.reload();
  //     }
  //   };

  //   window.addEventListener("message", onMessage);
  //   return () => window.removeEventListener("message", onMessage);
  // }, [dispatch]);

  const OAUTH_SUCCESS_KEY = "maxipitch_email_oauth_success";

  useEffect(() => {
    const BACKEND_ORIGIN = "https://api.maxipitch.com";
    const OAUTH_SUCCESS_KEY = "maxipitch_email_oauth_success";

    const handleOAuthSuccess = async (data) => {
      if (!data || data.type !== "MAXIPITCH_EMAIL_OAUTH_SUCCESS") return;

      try {
        if (window.oauthPopup && !window.oauthPopup.closed) {
          window.oauthPopup.close();
        }
      } catch (e) {
        console.error("Popup close failed:", e);
      } finally {
        window.oauthPopup = null;
      }

      try {
        await dispatch(fetchUserDetails()).unwrap();
      } catch (err) {
        console.error("Failed to refresh user details:", err);
      }

      setShowEmailConnect(false);
      window.location.reload();
    };

    const onMessage = (event) => {
      if (event.origin !== BACKEND_ORIGIN) return;
      handleOAuthSuccess(event.data);
    };

    const onStorage = (event) => {
      if (event.key !== OAUTH_SUCCESS_KEY || !event.newValue) return;

      try {
        const data = JSON.parse(event.newValue);
        handleOAuthSuccess(data);
      } catch (e) {
        console.error("Invalid OAuth payload:", e);
      }
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);

    try {
      const raw = localStorage.getItem(OAUTH_SUCCESS_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        handleOAuthSuccess(data);
      }
    } catch (e) {}

    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const sanitizeHtml = (html) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html || "", "text/html");
      ["script", "iframe", "style", "object", "embed", "link", "meta"].forEach(
        (tag) => doc.querySelectorAll(tag).forEach((el) => el.remove()),
      );
      doc.body.querySelectorAll("*").forEach((el) => {
        Array.from(el.attributes).forEach((attr) => {
          if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
          if (
            (attr.name === "href" || attr.name === "src") &&
            /^javascript:/i.test(attr.value || "")
          ) {
            el.removeAttribute(attr.name);
          }
        });
      });
      return doc.body.innerHTML;
    } catch {
      return "";
    }
  };

  const buildEmailTemplate = (subject, bodyPreviewHtml, footerPreviewHtml) => {
    const safeSubject = (subject || "").slice(0, 120);

    return `
  <!DOCTYPE html>
  <html>
  <head>
    <meta charset="UTF-8" />
    <title>${safeSubject}</title>
  </head>

  <body style="margin:0;padding:0;background:#f6f7fb;font-family:Poppins,Helvetica,Arial,sans-serif;">
    
    <table width="100%" cellpadding="0" cellspacing="0" style="background:#f6f7fb;padding:20px 0;">
      <tr>
        <td align="center">

          <table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:8px;padding:24px;">
            
            <tr>
              <td>
                <img src="/images/maxipitch.png" alt="MaxiPitch" width="120" style="display:block;margin-bottom:20px;" />
              </td>
            </tr>

            <tr>
              <td style="font-size:20px;font-weight:700;padding-bottom:10px;">
                ${safeSubject || "Your Email"}
              </td>
            </tr>

            <tr>
              <td>
                <hr style="border:0;border-top:1px solid #e5e7eb;" />
              </td>
            </tr>

            <tr>
              <td style="padding:20px 0;">
                ${bodyPreviewHtml || ""}
              </td>
            </tr>

            <tr>
              <td>
                <hr style="border:0;border-top:1px solid #e5e7eb;" />
              </td>
            </tr>

            <tr>
              <td>
                ${footerPreviewHtml || ""}
              </td>
            </tr>

          </table>

          <table width="600">
            <tr>
              <td align="center" style="font-size:12px;color:#6b7280;padding:16px;">
                You received this email because you interacted with MaxiPitch.
              </td>
            </tr>
          </table>

        </td>
      </tr>
    </table>

  </body>
  </html>
  `;
  };

  useEffect(() => {
    const html = buildEmailTemplate(subjectData, bodyHtml, footerHtml);
    setCompiledEmailHtml(html);
  }, [subjectData, bodyHtml, footerHtml]);

  /* ------------------------------ SAVE (RAW) ------------------------------ */
  const handleSaveTemplate = () => {
    if (!campaignName.trim()) {
      toast.warning("Please enter a campaign name");

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 1000);

      return;
    }

    const subject = cleanText(subjectData);
    const body = cleanText(bodyData);
    const footer = cleanText(footerData);

    const errors = [];

    if (!subject) errors.push("Subject");
    if (!body) errors.push("Body");
    if (!footer) errors.push("Footer");

    if (errors.length > 0) {
      toast.error(
        `${errors.join(", ")} ${errors.length > 1 ? "are" : "is"} required`,
      );
      return; // 🚫 STOP HERE
    }
    setTemplateSaved(true);
    setShowSaveConfirmModal(true);
  };

  const confirmSaveTemplate = async () => {
    setIsSaving(true);

    const payload = {
      subject_data: subjectData,
      body_data: bodyData,
      footer_data: footerData,
    };

    const action = selectedTemplateId
      ? updateCustomEmailTemplate({ id: selectedTemplateId, payload }) // UPDATE
      : saveCustomEmailTemplate(payload); // CREATE

    try {
      const saved = await dispatch(action).unwrap();

      toast.success(
        selectedTemplateId
          ? "Template updated successfully! ✅"
          : "Email template saved successfully! ✅",
      );

      // ✅ update local state immediately
      if (!selectedTemplateId && saved?.id) setSelectedTemplateId(saved.id);
      dispatch(fetchAllEmailTemplates());

      setSelectedTab("template");

      setBodyHtml(sanitizeHtml(payload.body_data || ""));
      setFooterHtml(sanitizeHtml(payload.footer_data || ""));

      setShowSaveConfirmModal(false);
      setTemplateSaved(true);

      setStep(3);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Save template error:", err);

      // ✅ show backend error if available
      const msg =
        err?.detail?.message ||
        err?.detail ||
        err?.message ||
        "Failed to save email template ❌";

      toast.error(msg);

      // ✅ keep modal open if you want user to retry
      // setShowSaveConfirmModal(false); // optional: remove this line if you want it to stay open
      setShowSaveConfirmModal(false);
    } finally {
      setIsSaving(false);
    }
  };

  /* --------------------------- AI (keeps RAW) ---------------------------- */
  const handleGenerateTemplateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please enter a prompt for AI template generation");
      return;
    }

    try {
      setAiGenerating(true);
      setAiReady(false);

      let finalPrompt = aiPrompt.trim();
      if (aiWebsiteUrl.trim()) {
        finalPrompt = `${aiPrompt.trim()} please review our website: ${aiWebsiteUrl.trim()}. Use the business model, services, and brand voice to create a well-structured, professional, and conversion-driven email that resonates with the target audience and encourages action;`;
      }

      const genPayload = {
        prompt: finalPrompt,
        user_prompt: sendPromptInput ? promptInput : "",
      };

      const res = await dispatch(createNewEmailTemplate(genPayload)).unwrap();
      const tpl = res?.data || res || {};

      const subject = tpl.subject_data || "";
      const body = tpl.body_data || "";
      const footer = tpl.footer_data || "";

      setSubjectData(subject);
      setBodyData(body);
      setFooterData(footer);

      setBodyHtml(sanitizeHtml(body));
      setFooterHtml(sanitizeHtml(footer));

      setSelectedTab("custom");
      setAiReady(true);

      // ✅ Auto-save to DB
      const savePayload = {
        subject_data: subject,
        body_data: body,
        footer_data: footer,
      };

      const saved = await dispatch(
        saveCustomEmailTemplate(savePayload),
      ).unwrap();

      // ✅ IMPORTANT: set id so future Save = UPDATE
      const newId = saved?.id || saved?.data?.id;
      if (newId) setSelectedTemplateId(newId);

      // ✅ IMPORTANT: allow Next button
      setTemplateSaved(true);

      dispatch(fetchAllEmailTemplates());

      toast.success("AI template generated & saved ✅");
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate/save AI template ❌");
    } finally {
      setAiGenerating(false);
    }
  };

  /* -------------------------- Wizard navigation -------------------------- */
  const handleNext = () => {
    if (step === 1) {
      if (!isEmailConnected) {
        setShowEmailConnect(true);
        return;
      }
      if (!selectedProvider) {
        toast.warning("Please select a connected email provider");
        return;
      }
    }
    if (step === 2 && campaignName.trim().length === 0) {
      toast.warning("Please enter a campaign name");
      return;
    }

    setStep((p) => Math.min(3, p + 1));
  };

  const handleBack = () => {
    setStep((prev) => {
      // when going from step 3 → 2, force "Create Email Template" tab
      if (prev === 3) {
        setSelectedTab("custom");
      }
      return Math.max(1, prev - 1);
    });
  };

  /* --------------------------- Load saved template ------------------------ */
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    const template = emailTemplates.find((t) => t.id === templateId);
    if (template) {
      setSubjectData(template.subject_data || "");
      setBodyData(template.body_data || "");
      setFooterData(template.footer_data || "");
      setBodyHtml(sanitizeHtml(template.body_data || ""));
      setFooterHtml(sanitizeHtml(template.footer_data || ""));
      setSelectedTab("custom"); // jump to editor view
      setAiReady(true);
    }
  };

  /* ------------------------- Draft snapshot & restore --------------------- */
  function snapshotDraft(extra = {}) {
    return {
      step,
      selectedProvider,
      campaignName,
      selectedTab,
      selectedTemplateId,
      subjectData,
      bodyData,
      footerData,
      bodyHtml,
      footerHtml,
      selectedPeople,
      intent: "idle",
      ...extra,
    };
  }

  useEffect(() => {
    const navDraft = location.state?.draft;
    const paymentSuccess = location.state?.paymentSuccess;

    if (navDraft) {
      setStep(navDraft.step ?? 1);
      setSelectedProvider(navDraft.selectedProvider ?? "");
      setCampaignName(navDraft.campaignName ?? "");
      setSelectedTab(navDraft.selectedTab ?? "custom");
      setSelectedTemplateId(navDraft.selectedTemplateId ?? "");
      setSubjectData(navDraft.subjectData ?? "");
      setBodyData(navDraft.bodyData ?? "");
      setFooterData(navDraft.footerData ?? "");
      setBodyHtml(navDraft.bodyHtml ?? "");
      setFooterHtml(navDraft.footerHtml ?? "");
      // setSelectedPeople(navDraft.selectedPeople ?? []);
    } else {
      const stored = loadDraft();
      if (stored) {
        setStep(stored.step ?? 1);
        setSelectedProvider(stored.selectedProvider ?? "");
        setCampaignName(stored.campaignName ?? "");
        setSelectedTab(stored.selectedTab ?? "custom");
        setSelectedTemplateId(stored.selectedTemplateId ?? "");
        setSubjectData(stored.subjectData ?? "");
        setBodyData(stored.bodyData ?? "");
        setFooterData(stored.footerData ?? "");
        setBodyHtml(stored.bodyHtml ?? "");
        setFooterHtml(stored.footerHtml ?? "");
        // setSelectedPeople(stored.selectedPeople ?? []);
      }
    }

    if (
      (paymentSuccess || loadDraft()?.paymentSuccess) &&
      (location.state?.intent === "send" || loadDraft()?.intent === "send")
    ) {
      clearDraft();
      saveDraft(snapshotDraft({ intent: "idle" }));
      confirmSendEmailsAfterReturn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function confirmSendEmailsAfterReturn() {
    handleCreateCampaign();
  }

  /* ------------------------- Send campaign (fixed) ------------------------ */
  const handleCreateCampaign = async () => {
    if (!campaignName.trim()) {
      toast.warning("Please enter a campaign name");
      return;
    }

    if (!isEmailConnected) {
      setShowEmailConnect(true);
      return;
    }

    if (leadItems.length === 0 && !sessionId) {
      toast.error("Looks like you haven’t searched for people yet");
      navigate("/search");
      return;
    }

    // ---------------------------
    // TEMPLATE SELECTION SAFETY
    // ---------------------------
    const selectedTemplate =
      selectedTab === "template"
        ? emailTemplates.find((t) => t.id === selectedTemplateId)
        : null;

    if (selectedTab === "template" && !selectedTemplateId) {
      toast.warning("Please select a template before launching");
      return;
    }

    // ---------------------------
    //  SUBJECT / BODY / FOOTER
    // ---------------------------
    const subject =
      selectedTab === "template"
        ? selectedTemplate?.subject_data || "Default Subject"
        : subjectData || "Default Subject";

    const body =
      selectedTab === "template"
        ? selectedTemplate?.body_data || "<p>No body provided</p>"
        : bodyHtml || "<p>No body provided</p>";

    const footer =
      selectedTab === "template"
        ? selectedTemplate?.footer_data || ""
        : footerHtml || "";

    // ---------------------------
    // FRONTEND VALIDATION
    // ---------------------------
    if (!subject.trim()) {
      toast.warning("Please provide a subject before launching");
      return;
    }

    if (!body.trim()) {
      toast.warning("Please provide an email body before launching");
      return;
    }

    setBulkSendLoading(true);

    // ---------------------------
    // FINAL PAYLOAD
    // ---------------------------
    const payload = {
      campaign_name: campaignName,
      session_id: sessionId,
      user_id: userId || sessionId,
      scheduled_at: new Date().toISOString(),
      is_custom_email: selectedTab === "custom",
      from_provider: selectedProvider,
      template_id: selectedTemplateId || null,
      subject_data: subject,
      body_data: body,
      footer_data: footer,
    };

    try {
      const created = await dispatch(createCampaignAPi(payload)).unwrap();
      toast.success("Campaign created successfully!");

      const sendPayload = {
        campaign_id: created.id,
        scheduled_at: created.created_at,
      };

      const send = await dispatch(sendBulkCampaignApi(sendPayload)).unwrap();

      if (!send.success && send.message === "Email provider not connected") {
        setShowOAuthPopup(true);
      } else {
        toast.success("Campaign launched successfully ✅");
      }

      clearDraft();
      clearCampaignDraft();
      setTimeout(() => navigate(`/campaign/${created.id}`), 2000);
    } catch (err) {
      console.error("❌ Campaign creation error:", err);

      // ✅ since you used rejectWithValue(err.response.data),
      // err will be the backend json directly
      const detail = err?.detail;

      // ✅ Special handling: blocking running campaign on provider
      if (detail?.provider && detail?.blocking_campaign_id) {
        const msg =
          `You already have a campaign running on ${String(detail.provider).toUpperCase()} ` +
          `("${detail.blocking_campaign_name}"). `;

        toast.error(msg, { duration: 8000 });
        return;
      }

      // ✅ Generic fallback
      toast.error(
        detail?.message || err?.message || "Error launching campaign",
      );
    } finally {
      setBulkSendLoading(false);
    }
  };
  const handleSendEmailsClick = () => setShowSendConfirmModal(true);

  const confirmSendEmails = () => {
    setShowSendConfirmModal(false);
    if (leadItems.length === 0 && !sessionId) {
      toast.error("Looks like you haven’t searched for people yet");
      navigate("/search");
      return; // ❗ STOP execution here
    }

    handleCreateCampaign();
  };

  const handleProviderChange = (providerName) => {
    setSelectedProvider(providerName);
  };

  if (bulkSendLoading) {
    return (
      <div className="flex flex-col items-center w-full opacity-100">
        <NormalLoader />
      </div>
    );
  }

  const handleTryEmail = async () => {
    if (!subjectData.trim() || !bodyData.trim() || !footerData.trim()) {
      toast.error("The subject, email body, and footer fields are mandatory");
      return;
    }

    const payload = {
      subject_data: subjectData.trim(),
      body_data: bodyData,
      footer_data: footerData,
    };

    try {
      const res = await dispatch(sendTrialEmail(payload)).unwrap();

      const success = res?.data?.success;
      const toEmail = res?.data?.to_email;
      const errorMessage = res?.message || "Failed to send test email";

      if (success) {
        setTimeout(() => {
          toast.success(
            `Test email sent successfully to ${toEmail}. Please check your inbox.`,
            { duration: 4500 },
          );
        }, 300);

        setHasContentChanged(false);
      } else {
        toast.error(errorMessage);
      }
    } catch (err) {
      // fallback for network / server / unexpected errors
      toast.error(
        err?.message || err || "Something went wrong while sending test email",
      );
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white  ">
      <Header />
      {/* 
    

      {/* Stepper */}
      <div className="flex items-center justify-center gap-6 max-md:gap-2 py-6 bg-gray-50 border-b max-md:p-4 mt-2 max-md:mt-4">
        {["Connect Email", "Campaign Details", "Review & Submit"].map(
          (label, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 max-md:w-10 flex items-center justify-center rounded-full border-2   ${
                  step === idx + 1
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-600"
                    : step > idx + 1
                      ? "bg-green-500 text-white border-green-500"
                      : "bg-white text-gray-500 border-gray-300"
                }`}
              >
                {step > idx + 1 ? <CheckCircle className="h-4 w-4" /> : idx + 1}
              </div>
              <span
                className={`text-sm ${
                  step === idx + 1
                    ? "font-semibold text-black max-md:text-sm"
                    : "text-gray-500"
                }`}
              >
                {label}
              </span>
            </div>
          ),
        )}
      </div>

      <main className="flex-1 w-[75%] mx-auto  py-8 max-md:py-8 px-4 max-md:w-full max-md:px-2">
        {step === 1 && (
          <div className="flex justify-end pb-4">
            <div className="relative inline-block group">
              <button
                onClick={() => {
                  setSpin(true);
                  dispatch(fetchUserDetails());
                  setTimeout(() => setSpin(false), 400);
                }}
                className="mx-3 text-base text-blue-600 flex items-center gap-2"
              >
                <RefreshCw
                  className={`h-5 w-5 max-md:h-3 max-md:w-3 transition-transform duration-300 ${
                    spin ? "rotate-180" : ""
                  }`}
                />
                <span>Refresh</span>
              </button>

              {/* Tooltip */}
              <div
                className="pointer-events-none absolute left-1/2 -translate-x-1/2 max-md:left-2 top-full mt-1 max-md:mt-0
                 whitespace-nowrap rounded-md bg-gray-900 px-3 py-1.5 text-xs text-white
                 opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100
                 transition-all duration-200 z-50"
              >
                Click here to refresh
              </div>
            </div>
          </div>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-4">
            {!isEmailConnected ? (
              <Card className="border border-gray-200 bg-gray-50 mb-8">
                <CardContent className="pt-6 pb-6">
                  <div className="text-center flex flex-col items-center">
                    <h3 className="text-base font-semibold text-black mb-2">
                      Connect Your Email
                    </h3>
                    <p className="text-sm text-black mb-4 max-w-sm mx-auto">
                      Connect the email account you want to use to send emails
                      to your leads (Gmail/Zoho/Outlook/SMTP)
                    </p>
                    <Button
                      onClick={() => setShowEmailConnect(true)}
                      className="main-button  text-white font-medium h-9"
                    >
                      Connect Now
                    </Button>

                    <div
                      style={{
                        height: "1.5px",
                        width: "50%",
                        margin: "40px 0px",
                        background:
                          "linear-gradient(to right, transparent, #2563EB, transparent)",
                      }}
                    />
                    <span className="">
                      If you have already connected click below to refresh now.
                    </span>
                    <button
                      onClick={() => {
                        setSpin(true);
                        dispatch(fetchUserDetails());
                        setTimeout(() => setSpin(false), 400);
                      }}
                      className="mx-3 text-base text-blue-600 flex items-center gap-2 pt-4"
                    >
                      <RefreshCw
                        className={`h-5 w-5 max-md:h-3 max-md:w-3 transition-transform duration-300 ${
                          spin ? "rotate-180" : ""
                        }`}
                      />
                      <span>Refresh</span>
                    </button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div>
                <Card className="border border-green-200 bg-green-50 mb-8  max-md:w-full">
                  <CardContent className="pt-6 pb-6   ">
                    <div className=" flex justify-between mb-4  ">
                      <h3 className="text-base font-semibold text-green-900 ">
                        Connected Email Providers
                      </h3>
                      <button
                        className=" text-blue-700 font-semibold text-sm max-md:text-xs  max-md:pl-4 underline flex justify-center items-center"
                        onClick={() => setShowEmailConnect(true)}
                      >
                        <MailPlus className="h-4 w-4 " />
                        <span className="px-2  max-md:px-1">
                          {" "}
                          Connect Emails
                        </span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {connectedProviders.map(([providerName, provider]) => (
                        <div
                          key={providerName}
                          className="flex items-center bg-white shadow-sm rounded-lg p-4 border"
                        >
                          <input
                            type="radio"
                            name="provider"
                            value={providerName}
                            checked={selectedProvider === providerName}
                            onChange={() => handleProviderChange(providerName)}
                            className="mr-3 h-4 w-4 "
                          />

                          <div className="flex items-center gap-3">
                            {providerName.toLowerCase() === "gmail" && (
                              <img
                                src={gamilIcon}
                                alt="Gmail"
                                className="h-6 w-6"
                              />
                            )}
                            {providerName.toLowerCase() === "outlook" && (
                              <img
                                src={microsoftIcon}
                                alt="Outlook"
                                className="h-6 w-6"
                              />
                            )}
                            {providerName.toLowerCase() === "yahoo" && (
                              <img
                                src={yahooIcon}
                                alt="Yahoo"
                                className="h-6 w-6"
                              />
                            )}
                            {providerName.toLowerCase() === "zoho" && (
                              <img
                                src={zohoIcon}
                                alt="Zoho"
                                className="h-6 w-6"
                              />
                            )}
                            {providerName.toLowerCase() === "smtp" && (
                              <img src={smtp} alt="SMTP" className="h-8 w-8" />
                            )}

                            <div>
                              <span className="font-medium text-gray-800 capitalize block">
                                {providerName}
                              </span>
                              <span className="text-sm text-gray-600">
                                {provider.from_email?.length > 20
                                  ? provider.from_email.slice(0, 20) + "..."
                                  : provider.from_email}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-3 ml-auto max-md:-mt-7">
                            {provider.connected === "true" ? (
                              <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-600">
                                Active
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-1 max-md:py-0 rounded-full bg-green-100 text-green-700">
                                Active
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 text-sm text-gray-700 font-semibold">
                      Selected :{" "}
                      {selectedProvider
                        ? selectedProvider.charAt(0).toUpperCase() +
                          selectedProvider.slice(1)
                        : "None"}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-4 w-full">
            {/* Campaign Name */}
            <Card className="border border-gray-200 bg-white w-full px-6 max-md:px-2  py-2">
              <CardContent className="px-1">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-black">
                    Campaign Name <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="campaign-name"
                    type="text"
                    placeholder="e.g. Q1 Product Launch Outreach"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    className="h-10  pl-4 pr-4 border border-gray-300 max-md:text-sm focus:border-black focus:ring-1 focus:ring-black rounded-lg text-black placeholder-gray-400"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Email Content */}
            <Card className="border border-gray-200 bg-white w-full px-6  max-md:px-1.5 ">
              <CardContent className="space-y-4 px-1 py-4">
                <Tabs
                  value={selectedTab}
                  onValueChange={setSelectedTab}
                  className="w-full"
                >
                  <TabsList className="grid w-full grid-cols-2  bg-white/80 gap-3 p-0 rounded-lg g-white/80 backdrop-blur-sm  shadow-lg border border-black/20 px-2 py-1">
                    <TabsTrigger
                      value="custom"
                      className="py-2 md:py-2.5 text-xs md:text-sm font-medium
               data-[state=active]:bg-gradient-to-r 
               data-[state=active]:from-blue-600 
               data-[state=active]:to-indigo-600 
               data-[state=active]:text-white
               rounded-none "
                    >
                      Create Email Template
                    </TabsTrigger>
                    <TabsTrigger
                      value="template"
                      className="py-2 md:py-2.5 text-xs md:text-sm font-medium
               data-[state=active]:bg-gradient-to-r 
               data-[state=active]:from-blue-600 
               data-[state=active]:to-indigo-600 
               data-[state=active]:text-white
               rounded-none "
                    >
                      Use Template
                    </TabsTrigger>
                  </TabsList>

                  {/* Use Template */}
                  <TabsContent value="template" className="space-y-6 mt-6">
                    <div className="bg-gradient-to-br from-gray-50 to-white max-md:rounded-none max-md:bg-none rounded-xl p-6 max-md:p-1 shadow-sm">
                      <div className="mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          Choose Email Template
                        </h3>
                        <p className="text-xs text-gray-600 mt-4">
                          Select from your saved templates or create a new one,
                          click{" "}
                          <span
                            className="underline cursor-pointer text-blue-600 font-semibold"
                            onClick={() => setSelectedTab("custom")}
                          >
                            Create Email Template
                          </span>{" "}
                          to customize your email.
                        </p>
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold text-gray-900">
                            Select Template{" "}
                            <span className="text-sm text-gray-600 mt-4 font-normal">
                              (Choose an existing email template subject)
                            </span>
                          </Label>

                          <Select
                            value={selectedTemplateId}
                            onValueChange={handleTemplateSelect}
                          >
                            <SelectTrigger
                              className="
                              h-12
                              w-full
                              px-4
                              border border-gray-300
                              rounded-lg
                              bg-white
                              text-sm
                              sm:text-base
                                                      "
                            >
                              <SelectValue placeholder="Choose a template to get started" />
                            </SelectTrigger>

                            <SelectContent
                              className="
                                    w-[--radix-select-trigger-width]
                                    max-w-[95vw]
                                    max-h-[60vh]
                                    overflow-y-auto
                                    rounded-lg
                                    border border-gray-400
                                    bg-white
                                    shadow-md
                                  "
                            >
                              <SelectGroup>
                                {emailTemplates.length > 0 ? (
                                  emailTemplates.map((template) => (
                                    <SelectItem
                                      key={template.id}
                                      value={template.id}
                                      className="
                                          px-4
                                          py-2
                                          cursor-pointer
                                          text-black
                                          rounded-md
                                          text-sm
                                          sm:text-base
                                          hover:bg-gray-100
                                          focus:bg-gray-100
                                        "
                                    >
                                      <div className="flex flex-col space-y-1">
                                        <span className=" font-semibold max-md:text-sm truncate text-wrap">
                                          {template.subject_data ||
                                            "No subject"}
                                        </span>
                                      </div>
                                    </SelectItem>
                                  ))
                                ) : (
                                  <div className="p-4 text-center text-gray-500">
                                    <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm mb-1">
                                      No templates found
                                    </p>
                                    <p className="text-xs text-gray-400">
                                      Create your first template in the Custom
                                      tab
                                    </p>
                                  </div>
                                )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>

                        {selectedTemplateId && (
                          <div className="space-y-3 ">
                            <Label className="text-sm font-semibold text-gray-900 flex items-center">
                              <Eye className="w-4 h-4 mr-2 text-green-600" />
                              Template Preview
                            </Label>
                            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl border-2 border-green-200 p-5 max-md:p-1.5 shadow-sm">
                              <div className="bg-white rounded-lg p-4 max-md:p-1 border border-gray-200  max-md:border-none shadow-sm">
                                <div className="mb-3">
                                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    Subject
                                  </span>
                                  <p className="text-sm font-semibold text-gray-900 mt-1">
                                    {subjectData ||
                                      emailTemplates.find(
                                        (t) => t.id === selectedTemplateId,
                                      )?.subject_data ||
                                      "No subject"}
                                  </p>
                                </div>
                                <div className="border-t border-gray-200 pt-3">
                                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    Body & Footer
                                  </span>
                                  <div className="text-sm text-gray-800 leading-relaxed max-h-96 overflow-y-auto mt-1 bg-gray-50 rounded-lg p-3 max-md:p-1.5">
                                    <div
                                      className="prose max-w-none"
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          bodyHtml ||
                                          sanitizeHtml(
                                            emailTemplates.find(
                                              (t) =>
                                                t.id === selectedTemplateId,
                                            )?.body_data || "",
                                          ),
                                      }}
                                    />
                                    <div
                                      className="prose max-w-none mt-4"
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          footerHtml ||
                                          sanitizeHtml(
                                            emailTemplates.find(
                                              (t) =>
                                                t.id === selectedTemplateId,
                                            )?.footer_data || "",
                                          ),
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="mt-3 flex items-center justify-between text-xs">
                                <span className="text-green-700 font-medium flex items-center">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Template loaded successfully
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Create Email Template */}
                  <TabsContent
                    value="custom"
                    className="space-y-6 mt-6 relative"
                  >
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl  p-6 max-md:p-2  pb-20 max-md:pb-[4rem]">
                      <div className="mb-6">
                        <div className="flex justify-between">
                          <h3 className="text-lg max-md:text-base font-semibold text-gray-900">
                            Create Custom Email Template
                          </h3>
                          <button
                            className="underline text-blue-500 font-semibold text-xs max-md:text-xxs"
                            onClick={() => setShowHowTo(true)}
                          >
                            How to Create AI Email Template
                          </button>
                        </div>

                        <p className="text-xs text-gray-600 mt-1">
                          Design your personalized email content with AI, Just
                          type your prompt or Raw email content and click on
                          "Create with AI"
                        </p>
                      </div>

                      {/* AI */}
                      <div className="space-y-3 mb-6">
                        {/* Textarea + Button */}
                        <div className="flex items-start gap-3 max-md:flex-col">
                          {/* Left side: input + textarea */}
                          <div className="flex flex-col gap-2 w-full md:flex-1">
                            {/* Website input (TOP) */}
                            <Label className="text-sm font-semibold text-gray-900">
                              Enter your business website (optional)
                            </Label>
                            <input
                              type="text"
                              value={aiWebsiteUrl}
                              placeholder="Enter your business website"
                              onChange={(e) => setAiWebsiteUrl(e.target.value)}
                              className="border border-gray-300 rounded-lg p-3 text-sm placeholder:text-sm w-full"
                            />
                            <Label className="text-sm font-semibold text-gray-900">
                              Provide some details about your Business
                              <span className="text-red-600">*</span>
                            </Label>
                            {/* Prompt textarea */}
                            <textarea
                              placeholder={
                                "Examples:\n1. We manufacture and distribute food products.\n2. We provide IT talent to IT companies"
                              }
                              value={aiPrompt}
                              onChange={(e) => setAiPrompt(e.target.value)}
                              className="border border-gray-300 text-sm rounded-lg p-3 h-24 max-md:h-32 resize-none w-full"
                            />
                          </div>

                          {/* Button */}
                        </div>

                        <div className="flex justify-center items-center">
                          <Button
                            type="button"
                            onClick={() => {
                              if (!aiPrompt?.trim()) {
                                toast.warning(
                                  "Please enter some details about your Business, after click on Create with AI",
                                );
                                return;
                              }

                              handleGenerateTemplateWithAI();
                            }}
                            disabled={aiGenerating}
                            className="main-button text-white mt-4 max-md:mt-2  
                                        max-md:self-end max-md:px-6 
                                        flex items-center justify-center gap-2
                                        disabled:opacity-100 disabled:cursor-not-allowed"
                          >
                            {aiGenerating ? (
                              <span className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 animate-pulse text-yellow-300" />
                                <span className="animate-pulse">
                                  Generating...
                                </span>
                              </span>
                            ) : (
                              <span className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5" />
                                Create with AI
                              </span>
                            )}
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-6 ">
                        {aiReady ? (
                          <>
                            {/* Subject */}
                            <div className="space-y-3 ">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-semibold text-gray-900">
                                  Subject Line
                                </Label>
                                <span
                                  className={`text-xs font-medium ${
                                    subjectData.length > 90
                                      ? "text-red-500"
                                      : subjectData.length > 70
                                        ? "text-yellow-600"
                                        : "text-gray-500"
                                  }`}
                                >
                                  {subjectData.length}/100
                                </span>
                              </div>
                              <Input
                                type="text"
                                placeholder="Enter a compelling subject line..."
                                value={subjectData}
                                onChange={(e) => {
                                  setSubjectData(e.target.value);
                                  setHasContentChanged(true);
                                }}
                                className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg transition-colors duration-200 max-md:text-sm"
                                maxLength={100}
                              />
                            </div>

                            {/* Body (WYSIWYG) */}
                            <div className="space-y-3 max-md: text-sm">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-semibold text-gray-900">
                                  Email Body
                                </Label>
                              </div>

                              <p className="text-xs text-gray-800 mt-1">
                                <span className="text-red-600 font-semibold">
                                  Note:
                                </span>{" "}
                                Make any changes you need. When you’re happy
                                with the final template, click Save below.
                              </p>

                              <HtmlEditor
                                value={bodyData}
                                onChange={(html) => {
                                  setBodyData(html);
                                  setHasContentChanged(true);
                                  setBodyHtml(html);
                                }}
                                placeholder="Write or paste content… supports HTML/MJML + placeholders like {{name}}."
                                minHeight={220}
                                sanitize={sanitizeHtml}
                              />
                            </div>

                            {/* Footer (WYSIWYG) */}
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-semibold text-gray-900">
                                  Email Footer{" "}
                                  {/* <span className="text-gray-500 font-normal">
                                    (Optional)
                                  </span> */}
                                </Label>
                              </div>
                              <HtmlEditor
                                className="text-sm"
                                value={footerData}
                                onChange={(html) => {
                                  setFooterData(html);
                                  setHasContentChanged(true);
                                  setFooterHtml(html);
                                }}
                                placeholder="Best regards,<br>{{sender_name}}"
                                minHeight={100}
                                sanitize={sanitizeHtml}
                              />
                            </div>

                            <div className="mt-6 border-t pt-4">
                              <Label className="text-sm font-semibold text-gray-900 mb-2 block">
                                Live Email Preview
                              </Label>

                              {/* Email Preview Background (Gmail-style) */}
                              <div className="bg-gray-200 py-4 max-md:py-2 rounded-lg border cursor-not-allowed">
                                <div
                                  className="bg-white rounded-lg shadow-sm overflow-hidden mx-auto"
                                  style={{
                                    maxWidth: "700px",
                                    fontFamily:
                                      'Arial, "Helvetica Neue", Helvetica, sans-serif',
                                  }}
                                >
                                  {/* ===== Read Header ===== */}
                                  <div className="border-b border-gray-300 px-6 max-md:px-3 py-4 max-md:py-2">
                                    <div className="text-base max-md:text-sm font-semibold text-gray-900">
                                      {subjectData ||
                                        "(subject will appear here)"}
                                    </div>

                                    <div className="flex items-center justify-between mt-3">
                                      <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center font-semibold">
                                          M
                                        </div>

                                        <div className="text-sm leading-tight">
                                          <p className="text-gray-900">
                                            From:you@example.com
                                          </p>
                                          <p className="text-gray-500">
                                            To: you@example.com
                                          </p>
                                        </div>
                                      </div>

                                      <div className="text-xs text-gray-500">
                                        {new Date().toLocaleDateString()}
                                      </div>
                                    </div>
                                  </div>

                                  {/* ===== Read Body ===== */}
                                  <div className="px-6 max-md:px-3 py-5 text-[15px] max-md:text-sm leading-normal text-gray-800">
                                    <div
                                      className="email-preview-content"
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          bodyHtml || "(body will render here)",
                                      }}
                                    />
                                  </div>

                                  {/* ===== YOUR EXISTING EMAIL FOOTER (UNCHANGED) ===== */}
                                  {footerHtml && (
                                    <div className="border-t px-6 py-4 max-md:px-3 max-md:py-2 text-sm text-gray-800">
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: footerHtml,
                                        }}
                                      />
                                    </div>
                                  )}

                                  {/* ===== Reader Footer (Actions) ===== */}
                                  {/* <div className="border-t bg-gray-50 px-6 max-md:px-3 py-3">
                                    <div className="flex flex-wrap gap-3">
                                      <button className="text-sm px-3 py-1.5 rounded-md border bg-white hover:bg-gray-100">
                                        Copy Subject
                                      </button>
                                      <button className="text-sm px-3 py-1.5 rounded-md border bg-white hover:bg-gray-100">
                                        Copy HTML
                                      </button>
                                      <button className="text-sm px-3 py-1.5 rounded-md border bg-white hover:bg-gray-100">
                                        Copy Plain Text
                                      </button>
                                    </div>

                                    <div className="text-xs text-gray-500 mt-2">
                                      Preview is sanitized to prevent script
                                      injection.
                                    </div>
                                  </div> */}
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="p-4 max-md:pb-10 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                            Use the AI generator above or select a template to
                            unlock editing
                          </div>
                        )}
                      </div>
                      {!hasContentChanged && (
                        <p className="text-sm text-gray-800 pt-6">
                          <span className="text-red-600 font-semibold">
                            Note:
                          </span>{" "}
                          Please make changes to try again and send another test
                          email.
                        </p>
                      )}

                      {aiReady && hasContentChanged && (
                        <p className="text-sm text-gray-800 mt-4">
                          <span className="text-red-600 font-semibold">
                            Note:
                          </span>{" "}
                          You can click Send Preview Email to check how this
                          email looks in your inbox. If everything appears as
                          expected, save the template and move on to the next
                          step.{" "}
                        </p>
                      )}
                      {/* Save */}
                      <div
                        className="absolute bottom-4 left-1/2 transform -translate-x-1/2
                flex gap-4 justify-center
                max-md:static max-md:translate-x-0 max-md:flex-col
                max-md:px-2 max-md:py-4 mt-4"
                      >
                        {/* Save as Template – always visible */}

                        {/* Try Email – visible only when AI is ready */}
                        {aiReady && (
                          <>
                            <Button
                              type="button"
                              onClick={handleTryEmail}
                              className="main-button text-white shadow-lg transition-all duration-200 transform hover:scale-105 max-md:w-full"
                              disabled={!hasContentChanged}
                            >
                              Send Preview Email
                            </Button>
                          </>
                        )}

                        {subjectData?.trim() &&
                        bodyData?.trim() &&
                        footerData?.trim() &&
                        footerData.replace(/<[^>]*>/g, "").trim() ? ( // Check if footer has actual content after stripping HTML tags
                          <Button
                            type="button"
                            onClick={handleSaveTemplate}
                            className="main-button text-white shadow-lg transition-all duration-200 transform hover:scale-105 max-md:w-full"
                            disabled={
                              !cleanText(subjectData) ||
                              !cleanText(bodyData) ||
                              !cleanText(footerData)
                            }
                          >
                            Save & Send Emails{" "}
                            <span className="pl-2">
                              {" "}
                              <SendHorizontal />
                            </span>
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="space-y-6 w-full">
            <Card className="border border-gray-200 bg-white w-full">
              <CardHeader className="pb-6">
                <CardTitle className="text-xl font-semibold text-black">
                  Review Campaign
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 max-md:p-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Campaign Name - Full width on mobile */}
                  <div className="p-3 bg-gray-50 rounded-lg sm:col-span-1 col-span-1">
                    <Label className="text-xs font-semibold text-black">
                      Campaign Name
                    </Label>
                    <p className="text-sm font-medium text-gray-800 mt-1 break-words">
                      {campaignName || "Untitled Campaign"}
                    </p>
                  </div>

                  {/* Wrapper for bottom two items on mobile */}
                  <div className="grid grid-cols-2 gap-4 sm:contents">
                    {/* Email Provider */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <Label className="text-xs font-semibold text-black">
                        Email Provider
                      </Label>
                      <p className="text-sm font-medium text-gray-800 mt-1 capitalize">
                        {selectedProvider || "Not selected"}
                      </p>
                    </div>

                    {/* Total Recipients */}
                    {/* <div className="p-3 bg-gray-50 rounded-lg">
                      <Label className="text-xs font-semibold text-black">
                        Total Recipients
                      </Label>
                      <p className="text-sm font-medium text-gray-800 mt-1">
                        {leadItems.length}
                      </p>
                    </div> */}
                  </div>
                </div>

                <div className=" bg-gray-50  max-md:bg-none rounded-lg px-6 max-md:px-2">
                  <Label className="text-sm font-semibold text-black mb-3 block pt-4">
                    Email Content
                  </Label>
                  <div className="bg-white  rounded border border-gray-200 max-md:border-none  space-y-2 p-4 max-md:p-1 pt-4">
                    <p className="text-xs text-gray-500">Subject:</p>
                    <p className="text-sm font-medium text-black">
                      {subjectData ||
                        emailTemplates.find((t) => t.id === selectedTemplateId)
                          ?.subject_data ||
                        "No subject"}
                    </p>

                    <p className="text-xs text-gray-500">Body:</p>
                    <div className="text-sm bg-white rounded border max-md:border-none p-3 max-md:p-1 max-h-80 overflow-y-auto overflow-hidden">
                      <div
                        className="prose max-w-none"
                        dangerouslySetInnerHTML={{
                          __html:
                            bodyHtml ||
                            sanitizeHtml(
                              emailTemplates.find(
                                (t) => t.id === selectedTemplateId,
                              )?.body_data || "",
                            ),
                        }}
                      />
                    </div>

                    <p className="text-xs text-gray-500">Footer:</p>
                    <div className="text-sm bg-white rounded border max-md:border-none p-3 max-md:p-1  max-h-20 overflow-y-auto overflow-hidden">
                      <div
                        className="prose max-w-none"
                        dangerouslySetInnerHTML={{
                          __html:
                            footerHtml ||
                            sanitizeHtml(
                              emailTemplates.find(
                                (t) => t.id === selectedTemplateId,
                              )?.footer_data || "",
                            ),
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-4 flex flex-col items-center max-md:pb-8">
                  <Button
                    className=" w-48  h-12 main-button  text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSendEmailsClick}
                    disabled={
                      isLoading ||
                      //  leadItems.length === 0 ||
                      !campaignName.trim() ||
                      !isEmailConnected
                    }
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mx-3  h-4 w-4 animate-spin" />
                        Sending Emails...
                      </>
                    ) : (
                      <>
                        Send Emails <SendHorizontal className="mx-3 h-4 w-4" />
                      </>
                    )}
                  </Button>

                  {(!isEmailConnected || !campaignName.trim()) && (
                    <div className="mt-3 text-center">
                      <p className="text-xs text-gray-500">
                        {!isEmailConnected && "Connect your email provider, "}
                        {!campaignName.trim() && "enter campaign name, "}
                        to continue
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Nav buttons */}
        <div className="flex mt-6">
          {step > 1 && (
            <Button
              variant="outline"
              className="main-button text-white px-10 max-md:px-4 py-3 flex items-center gap-2 max-md:ml-4  max-md:gap-0 hover:text-none"
              onClick={handleBack}
            >
              <ChevronLeft size={48} strokeWidth={3} />
              Back
            </Button>
          )}

          {step < 3 && (
            <div className="ml-auto">
              <Button
                className="main-button text-white px-10 max-md:px-4 py-3 flex items-center gap-2  max-md:gap-1
                   disabled:opacity-50 disabled:cursor-not-allowed max-md:mr-4"
                onClick={handleNext}
                disabled={
                  (step === 1 && (!isEmailConnected || !selectedProvider)) ||
                  (step === 2 && selectedTab === "custom" && !templateSaved)
                }
                title={
                  step === 1 && (!isEmailConnected || !selectedProvider)
                    ? "Select a connected email provider to proceed"
                    : step === 2 && selectedTab === "custom" && !templateSaved
                      ? "Save your template before proceeding"
                      : undefined
                }
              >
                Next
                <ChevronRight size={48} strokeWidth={3} />
              </Button>
            </div>
          )}
        </div>
      </main>

      <EmailConnectModal
        open={showEmailConnect}
        onClose={() => setShowEmailConnect(false)}
      />

      {/* SAVE CONFIRM */}
      <Dialog
        open={showSaveConfirmModal}
        onOpenChange={setShowSaveConfirmModal}
      >
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md p-4 sm:p-6 ">
          <DialogHeader>
            <DialogTitle className="mb-3 text-lg font-semibold max-md:text-base">
              Save Email Template
            </DialogTitle>
            <DialogDescription className="text-gray-800 text-base max-md:text-sm">
              Are you sure you want to save this email as a template? It will be
              available for future campaigns.
              <p className="mt-4 max-md:text-sm">
                <span className="text-red-600 text-center mr-1 font-bold  ">
                  *
                </span>
                Please review the email content carefully, as it will be sent in
                this campaign.
              </p>
              <p className="mt-1 max-md:text-sm">
                <span className="text-red-600 text-center mr-1 font-bold">
                  *
                </span>
                Before saving the template or sending the email, you can make
                any necessary changes to the content.
              </p>
              <p className="mt-1 ">
                <span className="text-red-600 text-center mr-1 font-bold">
                  *
                </span>
                Once you are satisfied that everything in the template is
                correct, click on Save Template.
              </p>
              <p className="mt-1 mb-2 max-md:text-sm">
                <span className="text-red-600 text-center mr-1 font-bold">
                  *
                </span>
                Please note that this email template will be used for sending
                all emails in this campaign.
              </p>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowSaveConfirmModal(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>

            <Button
              onClick={confirmSaveTemplate}
              className="main-button text-white flex items-center justify-center"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <FiLoader className="animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Yes, I agree"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SEND CONFIRM */}
      <Dialog
        open={showSendConfirmModal}
        onOpenChange={setShowSendConfirmModal}
      >
        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg p-4 sm:p-6 ">
          <DialogHeader>
            <DialogTitle>Confirm Send Emails</DialogTitle>
            <DialogDescription className="text-gray-800 text-sm  pt-1.5">
              <p className="text-base  font-semibold">
                Are you sure you want to send this campaign?
              </p>
              <p className="mt-6 text-sm">
                <span className="text-red-600 text-center mr-1 font-bold">
                  *
                </span>
                All emails will be sent from your email address.
              </p>
              <p className="mt-4  text-sm">
                <span className="text-red-600 text-center mr-1 font-bold">
                  *
                </span>
                It is important that you do not change your email password or
                modify OAuth access after connecting your email.
              </p>
              <p className="mt-4  text-sm">
                <span className="text-red-600 text-center mr-1 font-bold">
                  *
                </span>
                It is important that you do not change your email password or
                modify OAuth access after connecting your email.
              </p>
              <p className="mt-4  text-sm">
                <span className="text-red-600 text-center mr-1 font-bold">
                  *
                </span>
                If the email credentials or OAuth permissions are changed during
                an active campaign, all upcoming emails will be stopped.
              </p>
              <p className="mt-4 mb-2  text-sm">
                <span className="text-red-600 text-center mr-1 font-bold">
                  *
                </span>
                In such cases, we will be unable to send the remaining emails in
                the campaign.
              </p>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowSendConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSendEmails}
              className="main-button text-white"
            >
              Yes, I agree
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {showHowTo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm ">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowHowTo(false)}
          />

          {/* Modal Content */}
          <div className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl shadow-xl p-6 z-10 max-md:mx-4">
            {/* Close Button */}
            <button
              onClick={() => setShowHowTo(false)}
              className="absolute top-2 right-4 text-gray-500 hover:text-gray-800 text-2xl font-bold"
            >
              ×
            </button>

            <HowToUseEmailTemplate />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
